import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { securityInsightsApiRef } from '../api/apiRef';
import type { TaxonomyCategory, TaxonomyPillar } from '../api/types';

/**
 * Finding-taxonomy pillar/category code→label maps, shared by every table
 * that renders a Pillar/Category column (`GroupBySummaryPanel`,
 * `EntityGroupSecurityTab`) so they resolve codes to display labels
 * identically instead of each fetching/deriving their own copy.
 *
 * Fetched once per app session and cached at module scope — the same
 * idiom as `useCallerIdentity` — since `SingleViewPage` (its own direct
 * `getPillars`/`getCategories` calls plus a `GroupBySummaryPanel` that also
 * uses this hook) and `EntityGroupSecurityTab` (a `TeamFindingsTable` and a
 * `GroupBySummaryPanel` mounting/unmounting their own hook instances on
 * every Team⇄Asset toggle) would otherwise re-fire both endpoints on every
 * mount instead of sharing one in-flight fetch.
 */
let cachedPillarVocab: TaxonomyPillar[] = [];
let cachedCategoryVocab: TaxonomyCategory[] = [];
let attempted = false;
let inFlight: Promise<{
  pillarVocab: TaxonomyPillar[];
  categoryVocab: TaxonomyCategory[];
}> | null = null;

export function useTaxonomyLabels() {
  const api = useApi(securityInsightsApiRef);
  const [pillarVocab, setPillarVocab] =
    useState<TaxonomyPillar[]>(cachedPillarVocab);
  const [categoryVocab, setCategoryVocab] =
    useState<TaxonomyCategory[]>(cachedCategoryVocab);

  useEffect(() => {
    if (attempted) return undefined;
    let cancelled = false;
    if (!inFlight) {
      inFlight = Promise.all([api.getPillars(), api.getCategories()])
        .then(([p, c]) => {
          cachedPillarVocab = p;
          cachedCategoryVocab = c;
          attempted = true;
          return { pillarVocab: cachedPillarVocab, categoryVocab: cachedCategoryVocab };
        })
        .catch(
          () =>
            ({ pillarVocab: [], categoryVocab: [] }) as {
              pillarVocab: TaxonomyPillar[];
              categoryVocab: TaxonomyCategory[];
            },
        )
        .finally(() => {
          inFlight = null;
        });
    }
    inFlight.then(resolved => {
      if (cancelled) return;
      setPillarVocab(resolved.pillarVocab);
      setCategoryVocab(resolved.categoryVocab);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const pillarLabels = useMemo(
    () => new Map(pillarVocab.map(p => [p.code, p.label])),
    [pillarVocab],
  );
  const categoryLabels = useMemo(
    () => new Map(categoryVocab.map(c => [c.code, c.label])),
    [categoryVocab],
  );

  return { pillarVocab, categoryVocab, pillarLabels, categoryLabels };
}

/** Test-only: clears the module-level cache so each test starts fresh. */
export function _resetTaxonomyLabelsCacheForTests(): void {
  cachedPillarVocab = [];
  cachedCategoryVocab = [];
  attempted = false;
  inFlight = null;
}
